import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { KbQuickSuggestPanel } from "@/components/KbQuickSuggestPanel";
import { RenderedMessageBody } from "@/components/InlineCitationBadge";
import { parseInlineCitations, hasInlineCitations } from "@/lib/parseInlineCitations";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useChatContextNotes } from "@/contexts/ChatContextNotesContext";
import { api, extractErrorCode, getStoredToken, API } from "@/lib/api";
import { SmartBrainChips, suggestBrain } from "@/components/SmartBrain";
import { LegalTemplates } from "@/components/LegalTemplates";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  Plus,
  Send,
  Trash2,
  MessageSquare,
  Loader2,
  Sparkles,
  X,
  ArrowRight,
  Mic,
  Square,
  Scale,
  BookmarkPlus,
  BookmarkCheck,
  BookMarked,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

// Flat list of all (provider, model) variants exposed to the user.
// `groupKey` keeps the select visually organised by provider.
// `gated` flag = visible to all but only usable on legal_pro/business tiers
//   (server transparently downgrades to default for other tiers).
// `badge` flag = render a small label next to the option name.
const MODELS = [
  { id: "anthropic|claude-sonnet-4-6", provider: "anthropic", model: "claude-sonnet-4-6", labelKey: "chat.model_claude_sonnet_46", groupKey: "chat.group_claude", badge: "NEW" },
  { id: "anthropic|claude-opus-4-8", provider: "anthropic", model: "claude-opus-4-8", labelKey: "chat.model_claude_opus_48", groupKey: "chat.group_claude", gated: true, badge: "PRO" },
  { id: "anthropic|claude-fable-5", provider: "anthropic", model: "claude-fable-5", labelKey: "chat.model_claude_fable_5", groupKey: "chat.group_claude", gated: true, badge: "BETA" },
  { id: "anthropic|claude-sonnet-4-5-20250929", provider: "anthropic", model: "claude-sonnet-4-5-20250929", labelKey: "chat.model_claude_sonnet", groupKey: "chat.group_claude" },
  { id: "anthropic|claude-haiku-4-5-20251001", provider: "anthropic", model: "claude-haiku-4-5-20251001", labelKey: "chat.model_claude_haiku", groupKey: "chat.group_claude" },
  { id: "anthropic|claude-opus-4-5-20251101", provider: "anthropic", model: "claude-opus-4-5-20251101", labelKey: "chat.model_claude_opus", groupKey: "chat.group_claude", gated: true },
  { id: "openai|gpt-5.2", provider: "openai", model: "gpt-5.2", labelKey: "chat.model_gpt_52", groupKey: "chat.group_openai" },
  { id: "openai|gpt-4o", provider: "openai", model: "gpt-4o", labelKey: "chat.model_gpt_4o", groupKey: "chat.group_openai" },
  { id: "openai|gpt-4.1-mini", provider: "openai", model: "gpt-4.1-mini", labelKey: "chat.model_gpt_41_mini", groupKey: "chat.group_openai" },
  { id: "gemini|gemini-3-flash-preview", provider: "gemini", model: "gemini-3-flash-preview", labelKey: "chat.model_gemini_flash", groupKey: "chat.group_gemini" },
  { id: "gemini|gemini-3.1-pro-preview", provider: "gemini", model: "gemini-3.1-pro-preview", labelKey: "chat.model_gemini_pro", groupKey: "chat.group_gemini" },
];
const DEFAULT_MODEL_ID_BY_PROVIDER = {
  anthropic: "anthropic|claude-sonnet-4-6",
  openai: "openai|gpt-5.2",
  gemini: "gemini|gemini-3-flash-preview",
};

export default function ChatPage() {
  const { t } = useI18n();
  const { refresh, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [modelId, setModelId] = useState(() => {
    const fromUrl = searchParams.get("provider");
    if (["anthropic", "openai", "gemini"].includes(fromUrl)) {
      return DEFAULT_MODEL_ID_BY_PROVIDER[fromUrl];
    }
    return DEFAULT_MODEL_ID_BY_PROVIDER.anthropic;
  });
  const [sending, setSending] = useState(false);
  const [dismissedHints, setDismissedHints] = useState(new Set());
  const [convListOpen, setConvListOpen] = useState(false);
  // Legal mode (specialized IT/EU legal assistant system prompt).
  // Legal Pro tier subscribers get it ON by default — initial value is set
  // after `user` resolves via the imperative effect below. Other users can
  // still opt in per-chat with the header toggle.
  const [legalMode, setLegalMode] = useState(false);
  // Auto-include: when true the backend auto-injects top-3 KB notes as context
  // for every chat send (zero-click ambient mode). Only visible to legal_pro
  // /business users, and persisted across reloads via localStorage.
  const [kbAutoInclude, setKbAutoInclude] = useState(() => {
    try {
      return localStorage.getItem("neuracom_chat_kb_auto_include_v1") === "1";
    } catch (e) {
      return false;
    }
  });
  // Save to Legal KB state — message_id → "saving" | "saved"
  const [kbSavedMsgs, setKbSavedMsgs] = useState({});
  const canSaveToKB = user && ["legal_pro", "business"].includes(user.subscription_tier);
  // Selected KB notes to inject as context for the next chat turn (cross-page state)
  const { notes: ctxKbNotes, addNote: addCtxNote, removeNote: removeCtxNote, clearNotes: clearCtxNotes, max: maxCtxKb } = useChatContextNotes();
  // Voice input (Whisper)
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const scrollRef = useRef(null);

  // Derive provider+model from the single modelId for cleaner downstream code
  const [provider, model] = modelId.split("|");
  const setProvider = (nextProvider) => {
    setModelId(DEFAULT_MODEL_ID_BY_PROVIDER[nextProvider] || modelId);
  };

  // Clean up the URL after consuming the provider param
  useImperativeLoad((() => {
    return () => {
      if (searchParams.get("provider")) {
        const next = new URLSearchParams(searchParams);
        next.delete("provider");
        setSearchParams(next, { replace: true });
      }
    };
  })(), []);

  // Compute current brain hint based on the draft input
  const brainHint = React.useMemo(() => {
    const s = suggestBrain(input, provider);
    if (!s) return null;
    if (dismissedHints.has(`${provider}->${s.provider}`)) return null;
    return s;
  }, [input, provider, dismissedHints]);

  const acceptBrainHint = () => {
    if (brainHint) setProvider(brainHint.provider);
  };
  const dismissBrainHint = () => {
    if (brainHint) {
      setDismissedHints((prev) => {
        const next = new Set(prev);
        next.add(`${provider}->${brainHint.provider}`);
        return next;
      });
    }
  };

  const handlePickChip = ({ provider: chipProvider, prompt }) => {
    setProvider(chipProvider);
    setInput(prompt);
  };

  const loadSessions = async () => {
    const r = await api.get("/chat/sessions");
    setSessions(r.data.sessions || []);
  };

  // Imperative session activator — used by click handlers and the initial
  // mount path. Sets activeId and loads messages in one event-driven step,
  // so we don't need an effect-driven loader (which trips React 19's
  // strict set-state-in-effect rule).
  const activateSession = async (sessionId) => {
    setActiveId(sessionId);
    if (!sessionId) {
      setMessages([]);
      // Keep legalMode as-is so users can compose a new legal chat without
      // having to re-toggle. (When they explicitly start a new "regular" chat
      // they can flip the toggle off.)
      return;
    }
    const r = await api.get(`/chat/sessions/${sessionId}/messages`);
    setMessages(r.data.messages || []);
    const sessProvider = r.data.session?.provider || "anthropic";
    setModelId(DEFAULT_MODEL_ID_BY_PROVIDER[sessProvider] || DEFAULT_MODEL_ID_BY_PROVIDER.anthropic);
    setLegalMode(!!r.data.session?.legal_mode);
  };

  useImperativeLoad(() => { loadSessions(); }, []);

  // Legal Pro tier subscribers: turn Legal Mode ON by default the first time
  // they hit /chat. Runs once when `user` resolves; users keep full control of
  // the toggle thereafter.
  const [legalDefaultApplied, setLegalDefaultApplied] = useState(false);
  useImperativeLoad(() => {
    if (legalDefaultApplied) return;
    if (user?.subscription_tier === "legal_pro" && !activeId) {
      setLegalMode(true);
    }
    if (user) setLegalDefaultApplied(true);
  }, [user, activeId, legalDefaultApplied]);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setSending(true);

    // Optimistic user message
    const optimisticUserMsg = {
      message_id: `tmp_user_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    // Streaming assistant placeholder — we'll mutate this as deltas arrive
    const streamingAssistantId = `tmp_asst_${Date.now()}`;
    const streamingAssistant = {
      message_id: streamingAssistantId,
      role: "assistant",
      content: "",
      streaming: true,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimisticUserMsg, streamingAssistant]);

    const appendDelta = (chunk) => {
      setMessages((m) =>
        m.map((msg) =>
          msg.message_id === streamingAssistantId
            ? { ...msg, content: (msg.content || "") + chunk }
            : msg
        )
      );
    };
    const finalizeAssistant = (finalMsg) => {
      setMessages((m) =>
        m.map((msg) =>
          msg.message_id === streamingAssistantId
            ? { ...finalMsg, streaming: false }
            : msg
        )
      );
    };
    const rollback = () => {
      setMessages((m) =>
        m.filter(
          (x) =>
            x.message_id !== optimisticUserMsg.message_id &&
            x.message_id !== streamingAssistantId
        )
      );
    };

    try {
      const token = getStoredToken() || "";

      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          chat_session_id: activeId || undefined,
          message: text,
          provider,
          model,
          legal_mode: legalMode,
          // Inject KB notes only when allowed-tier AND any selected. Backend
          // double-checks tier so it's safe to always send if present.
          kb_note_ids: canSaveToKB && ctxKbNotes.length > 0
            ? ctxKbNotes.map((n) => n.note_id)
            : undefined,
          // Zero-click ambient context: server picks top-3 KB notes for this
          // message. Tier-gated server-side, so safe to always send the flag.
          kb_auto_include: canSaveToKB && kbAutoInclude ? true : undefined,
        }),
      });

      if (!resp.ok) {
        // Quota / auth errors — parse JSON body to surface the error_code
        let errCode = "UNKNOWN_ERROR";
        try {
          const j = await resp.json();
          errCode = j?.detail?.error_code || errCode;
        } catch (e) { /* ignore */ }
        const translated = t(`chat.err_${errCode}`);
        const msg = typeof translated === "string" && !translated.startsWith("chat.")
          ? translated : `Error: ${errCode}`;
        toast.error(msg);
        rollback();
        return;
      }

      // Consume the SSE stream
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let receivedNewSessionId = null;
      let receivedDone = false;
      let streamError = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        // Keep trailing partial line in the buffer for the next iteration
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (!json) continue;
          let evt;
          try {
            evt = JSON.parse(json);
          } catch (e) {
            continue; // skip malformed line, keep streaming
          }
          if (evt.type === "meta") {
            receivedNewSessionId = evt.chat_session_id;
            if (evt.model_fallback) {
              const [from, to] = evt.model_fallback.split("→");
              toast.info(t("chat.model_fallback_toast", { from: from || "", to: to || "" }));
            }
          } else if (evt.type === "delta") {
            appendDelta(evt.content || "");
          } else if (evt.type === "done") {
            finalizeAssistant(evt.assistant_message);
            receivedNewSessionId = receivedNewSessionId || evt.chat_session_id;
            receivedDone = true;
          } else if (evt.type === "error") {
            streamError = evt.error_code || "UNKNOWN_ERROR";
          }
        }
      }

      if (streamError) {
        const translated = t(`chat.err_${streamError}`);
        toast.error(
          typeof translated === "string" && !translated.startsWith("chat.")
            ? translated
            : `Error: ${streamError}`
        );
        rollback();
        return;
      }
      if (!receivedDone) {
        toast.error(`Error: STREAM_INCOMPLETE`);
        rollback();
        return;
      }
      if (receivedNewSessionId && receivedNewSessionId !== activeId) {
        setActiveId(receivedNewSessionId);
        loadSessions();
      }
      refresh();
    } catch (err) {
      const code = err?.message || "UNKNOWN_ERROR";
      toast.error(`Error: ${code}`);
      rollback();
    } finally {
      setSending(false);
    }
  };

  // ────────────────────────── Voice input (Whisper) ──────────────────────────
  // Picks a mimeType that the current browser actually supports.
  // - Chrome/Firefox/Edge: audio/webm (default)
  // - iOS Safari: audio/mp4 (the ONLY format MediaRecorder produces on iOS)
  // - Android Chrome: audio/webm with opus
  // Returns null when no supported type is found → we fall back to letting
  // the browser pick a default (rare, mostly older Android WebViews).
  const _pickAudioMimeType = () => {
    if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return null;
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/ogg;codecs=opus",
      "audio/ogg",
    ];
    return candidates.find((c) => MediaRecorder.isTypeSupported(c)) || null;
  };

  const startRecording = async () => {
    if (recording || transcribing) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error(t("chat.mic_unsupported"));
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      toast.error(t("chat.mic_unsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordedChunksRef.current = [];
      // Pick a codec the browser supports — critical for iOS Safari
      // (which only produces audio/mp4) vs Chrome (audio/webm).
      const mimeType = _pickAudioMimeType();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const finalType = mr.mimeType || mimeType || "audio/webm";
        const blob = new Blob(recordedChunksRef.current, { type: finalType });
        recordedChunksRef.current = [];
        if (blob.size < 800) {
          // Less than ~0.5s of audio — likely a misclick
          toast.error(t("chat.mic_too_short"));
          return;
        }
        await sendForTranscription(blob);
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      // NotAllowedError: user denied permission
      // NotFoundError: no mic
      // NotReadableError: mic in use by another app
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        toast.error(t("chat.mic_denied"));
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        toast.error(t("chat.mic_no_device"));
      } else {
        toast.error(t("chat.mic_failed"));
      }
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    }
    setRecording(false);
  };

  const sendForTranscription = async (blob) => {
    setTranscribing(true);
    try {
      // Map mime type → filename extension (Whisper auto-detects format
      // but having the correct extension makes the upload more robust).
      const typeNoParams = (blob.type || "").split(";")[0].toLowerCase();
      const ext = (
        typeNoParams === "audio/webm" ? "webm" :
        typeNoParams === "audio/mp4" ? "m4a" :
        typeNoParams === "audio/ogg" ? "ogg" :
        typeNoParams === "audio/wav" ? "wav" :
        typeNoParams === "audio/mpeg" ? "mp3" :
        "webm"
      );
      const form = new FormData();
      form.append("audio", blob, `voice.${ext}`);
      const r = await api.post("/chat/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcript = (r.data.text || "").trim();
      if (!transcript) {
        toast.error(t("chat.mic_no_speech"));
      } else {
        setInput((cur) => (cur ? `${cur} ${transcript}` : transcript));
      }
    } catch (err) {
      const code = extractErrorCode(err);
      const translated = t(`chat.err_${code}`);
      const msg = typeof translated === "string" && !translated.startsWith("chat.") ? translated : t("chat.mic_failed");
      toast.error(msg);
    } finally {
      setTranscribing(false);
    }
  };

  const handleIncludeSuggestedNote = (note) => {
    const r = addCtxNote(note);
    if (r.added) {
      toast.success(t("chat.kb_suggest_added"));
    } else if (r.reason === "max") {
      toast.error(t("legal_kb.use_in_chat_max", { max: maxCtxKb }));
    } else if (r.reason === "duplicate") {
      toast.info(t("legal_kb.use_in_chat_already"));
    }
  };

  const toggleAutoInclude = () => {
    setKbAutoInclude((v) => {
      const next = !v;
      try {
        localStorage.setItem("neuracom_chat_kb_auto_include_v1", next ? "1" : "0");
      } catch (e) {
        // localStorage may be disabled — toggle still works in memory
      }
      return next;
    });
  };

  const handleSaveMessageToKB = async (message) => {
    if (!canSaveToKB || message.role !== "assistant") return;
    setKbSavedMsgs((s) => ({ ...s, [message.message_id]: "saving" }));
    try {
      const titleSeed = message.content.replace(/\s+/g, " ").trim().slice(0, 80);
      await api.post("/legal/knowledge", {
        title: titleSeed || t("legal_kb.save_chat_title"),
        body: message.content,
        source: "chat",
        source_ref: activeId,
        citation: message.model ? `NEURACOM Legal · ${message.model}` : null,
        tags: legalMode ? ["chat", "legal_mode"] : ["chat"],
      });
      setKbSavedMsgs((s) => ({ ...s, [message.message_id]: "saved" }));
      toast.success(t("legal_kb.toast_created"));
    } catch (err) {
      setKbSavedMsgs((s) => {
        const next = { ...s };
        delete next[message.message_id];
        return next;
      });
      const code = extractErrorCode(err);
      toast.error(t(`legal_kb.err_${code}`, { _default: t("legal_kb.err_save") }));
    }
  };


  const handleDeleteSession = async (id) => {
    if (!window.confirm(t("chat.delete_confirm"))) return;
    await api.delete(`/chat/sessions/${id}`);
    if (activeId === id) {
      setActiveId(null);
      setMessages([]);
    }
    loadSessions();
  };

  return (
    <div className="flex h-full" data-testid="chat-page">
      {/* Mobile backdrop for conv list */}
      {convListOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setConvListOpen(false)}
          aria-hidden
        />
      )}
      {/* Conversations sidebar — drawer on mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-slate-800 bg-[#070924] transition-transform duration-300 lg:static lg:translate-x-0 ${
          convListOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="chat-conv-list"
      >
        <div className="border-b border-slate-800 p-3">
          <Button
            onClick={() => {
              activateSession(null);
              setInput("");
              setConvListOpen(false);
            }}
            className="w-full justify-start rounded-lg bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
            data-testid="chat-new-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("chat.new_chat")}
          </Button>
        </div>
        <ScrollArea className="flex-1">
          <ul className="space-y-1 p-2">
            {sessions.map((s) => (
              <li
                key={s.chat_session_id}
                className={`group flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                  activeId === s.chat_session_id
                    ? "bg-[#9ACD32]/15 text-[#9ACD32]"
                    : "text-slate-300 hover:bg-slate-800/40"
                }`}
                onClick={() => { activateSession(s.chat_session_id); setConvListOpen(false); }}
                data-testid={`chat-session-${s.chat_session_id}`}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-60" />
                <span className="line-clamp-1 flex-1">{s.title}</span>
                <button
                  className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSession(s.chat_session_id);
                  }}
                  data-testid={`chat-delete-${s.chat_session_id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </ScrollArea>
      </aside>

      {/* Chat area */}
      <div className="flex flex-1 flex-col min-w-0">
        <header className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setConvListOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-slate-300 hover:bg-slate-800 hover:text-white lg:hidden"
              aria-label="Open conversations"
              data-testid="chat-convs-toggle"
            >
              <MessageSquare className="h-4 w-4" />
            </button>
            <Sparkles className="hidden h-4 w-4 text-[#9ACD32] sm:block" />
            <span className="text-sm font-semibold text-white">NEURACOM Chat</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden text-xs uppercase tracking-wider text-slate-500 sm:inline">{t("chat.model")}</span>
            <Select value={modelId} onValueChange={setModelId} disabled={sending}>
              <SelectTrigger className="h-9 w-[180px] rounded-full border-slate-700 bg-slate-900 text-xs sm:w-[240px] sm:text-sm" data-testid="chat-model-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["chat.group_claude", "chat.group_openai", "chat.group_gemini"].map((gk) => (
                  <React.Fragment key={gk}>
                    <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {t(gk)}
                    </div>
                    {MODELS.filter((m) => m.groupKey === gk).map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`chat-model-option-${m.id.replace(/[|.]/g, "-")}`}>
                        <span className="flex items-center gap-2">
                          {t(m.labelKey)}
                          {m.badge && (
                            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-extrabold tracking-wider ${
                              m.badge === "NEW" ? "bg-[#9ACD32]/20 text-[#9ACD32]"
                              : m.badge === "BETA" ? "bg-orange-500/20 text-orange-400"
                              : "bg-[#D4A937]/20 text-[#F4C955]"
                            }`}>
                              {m.badge}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
            <a
              href="/brains"
              target="_blank"
              rel="noreferrer"
              className="hidden items-center gap-1 rounded-full border border-slate-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:border-[#9ACD32]/50 hover:text-[#9ACD32] sm:inline-flex"
              data-testid="chat-brains-help-link"
            >
              <Sparkles className="h-3 w-3" />
              {t("chat.help_which_brain")}
            </a>
            <button
              type="button"
              onClick={() => setLegalMode((v) => !v)}
              disabled={sending}
              aria-pressed={legalMode}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-3 sm:py-1.5 sm:text-xs ${
                legalMode
                  ? "border-[#D4A937] bg-[#D4A937]/15 text-[#F4C955]"
                  : "border-slate-700 text-slate-400 hover:border-[#D4A937]/40 hover:text-[#F4C955]"
              }`}
              data-testid="chat-legal-mode-toggle"
              title={legalMode ? t("chat.legal_mode_active") : t("chat.legal_mode_inactive")}
            >
              <Scale className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              <span className="hidden sm:inline">
                {legalMode ? t("chat.legal_mode_on") : t("chat.legal_mode_off")}
              </span>
              <span className="sm:hidden">{t("chat.legal_short")}</span>
            </button>
            {canSaveToKB && (
              <button
                type="button"
                onClick={toggleAutoInclude}
                disabled={sending}
                aria-pressed={kbAutoInclude}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-3 sm:py-1.5 sm:text-xs ${
                  kbAutoInclude
                    ? "border-[#9ACD32] bg-[#9ACD32]/15 text-[#9ACD32]"
                    : "border-slate-700 text-slate-400 hover:border-[#9ACD32]/40 hover:text-[#9ACD32]"
                }`}
                data-testid="chat-auto-include-toggle"
                title={kbAutoInclude ? t("chat.auto_include_active") : t("chat.auto_include_inactive")}
              >
                <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                <span className="hidden sm:inline">
                  {kbAutoInclude ? t("chat.auto_include_on") : t("chat.auto_include_off")}
                </span>
                <span className="sm:hidden">{t("chat.auto_short")}</span>
              </button>
            )}
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-3xl px-6 py-8">
            {messages.length === 0 && (
              <div className="flex h-full min-h-[40vh] flex-col items-center justify-center text-center" data-testid="chat-empty-state">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#9ACD32]/10">
                  <Sparkles className="h-8 w-8 text-[#9ACD32]" />
                </div>
                <p className="font-display text-2xl font-bold text-white">{t("chat.no_messages")}</p>
                <p className="mt-2 text-sm text-slate-400">{t("chat.smart_chips_hint")}</p>
                <div className="mt-8 w-full max-w-2xl">
                  <SmartBrainChips onPick={handlePickChip} />
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.message_id}
                className={`message-in mb-6 flex gap-4 ${m.role === "user" ? "justify-end" : ""}`}
                data-testid={`chat-message-${m.role}`}
              >
                {m.role === "assistant" && (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#9ACD32] text-xs font-bold text-[#05073B]">
                    N
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-5 py-3.5 text-[15px] leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#1A1D7A] text-white"
                      : "bg-slate-900/60 text-slate-100"
                  }`}
                >
                  {m.role === "assistant" && hasInlineCitations(m.content) ? (
                    <div data-testid={`msg-body-${m.message_id}`}>
                      <RenderedMessageBody
                        text={m.content}
                        parsed={parseInlineCitations(m.content)}
                      />
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap" data-testid={`msg-body-${m.message_id}`}>
                      {m.content}
                    </div>
                  )}
                  {m.role === "assistant" && (
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">
                        {m.model}
                      </div>
                      {canSaveToKB && legalMode && !sending && (
                        <button
                          type="button"
                          onClick={() => handleSaveMessageToKB(m)}
                          disabled={kbSavedMsgs[m.message_id] === "saving" || kbSavedMsgs[m.message_id] === "saved"}
                          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:cursor-default ${
                            kbSavedMsgs[m.message_id] === "saved"
                              ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
                              : "border-[#D4A937]/40 bg-[#D4A937]/10 text-[#F4C955] hover:border-[#D4A937] hover:bg-[#D4A937]/20"
                          }`}
                          data-testid={`chat-save-kb-${m.message_id}`}
                          title={t("legal_kb.save_chat_btn")}
                        >
                          {kbSavedMsgs[m.message_id] === "saving" ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : kbSavedMsgs[m.message_id] === "saved" ? (
                            <BookmarkCheck className="h-3 w-3" />
                          ) : (
                            <BookmarkPlus className="h-3 w-3" />
                          )}
                          <span>{t("legal_kb.save_chat_btn")}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="message-in flex items-center gap-3 text-sm text-slate-400" data-testid="chat-thinking">
                <Loader2 className="h-4 w-4 animate-spin text-[#9ACD32]" />
                <span>{t("chat.thinking")}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-800 bg-[#0A0C1F] p-4">
          <div className="mx-auto max-w-3xl">
            {brainHint && (
              <div
                className="message-in mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#9ACD32]/30 bg-[#9ACD32]/5 px-4 py-3 text-sm"
                data-testid="chat-brain-hint"
              >
                <Sparkles className="h-4 w-4 shrink-0 text-[#9ACD32]" />
                <span className="flex-1 text-slate-200">
                  {t(brainHint.reasonKey, { brain: brainHint.providerLabel })}
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    onClick={acceptBrainHint}
                    size="sm"
                    className="h-8 rounded-full bg-[#9ACD32] px-3 text-xs font-bold text-[#05073B] hover:bg-[#A8DF38]"
                    data-testid="chat-brain-hint-accept"
                  >
                    {t("chat.brain_hint_switch", { brain: brainHint.providerLabel })}
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                  <button
                    onClick={dismissBrainHint}
                    className="rounded-full p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
                    aria-label={t("common.cancel")}
                    data-testid="chat-brain-hint-dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
            {legalMode && (
              <>
                <LegalTemplates
                  onPick={(prompt) => setInput(prompt)}
                  disabled={sending || transcribing}
                />
                <div
                  className="mb-3 flex items-start gap-2 rounded-xl border border-[#D4A937]/30 bg-[#D4A937]/10 px-3 py-2 text-[11px] text-[#F4C955]"
                  data-testid="chat-legal-disclaimer"
                >
                  <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="leading-relaxed">{t("chat.legal_disclaimer")}</span>
                </div>
              </>
            )}
            <KbQuickSuggestPanel
              draft={input}
              enabled={canSaveToKB && legalMode && !sending && !kbAutoInclude}
              excludeIds={ctxKbNotes.map((n) => n.note_id)}
              onInclude={handleIncludeSuggestedNote}
              className="mb-2"
            />
            {canSaveToKB && kbAutoInclude && (
              <div
                className="mb-2 flex items-center gap-2 rounded-xl border border-[#9ACD32]/30 bg-[#9ACD32]/8 px-3 py-2 text-[11px] text-[#9ACD32]"
                data-testid="chat-auto-include-banner"
              >
                <Zap className="h-3.5 w-3.5 shrink-0" />
                <span>{t("chat.auto_include_banner")}</span>
              </div>
            )}
            {canSaveToKB && ctxKbNotes.length > 0 && (
              <div
                className="mb-3 rounded-xl border border-[#9ACD32]/30 bg-[#9ACD32]/10 px-3 py-2.5"
                data-testid="chat-kb-context-bar"
              >
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#9ACD32]">
                    <BookMarked className="h-3 w-3" />
                    {t("chat.kb_context_title", { count: ctxKbNotes.length })}
                  </span>
                  <button
                    type="button"
                    onClick={clearCtxNotes}
                    className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-white"
                    data-testid="chat-kb-context-clear"
                  >
                    {t("chat.kb_context_clear")}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ctxKbNotes.map((n) => (
                    <span
                      key={n.note_id}
                      className="inline-flex max-w-[260px] items-center gap-1.5 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] text-slate-200"
                      data-testid={`chat-kb-chip-${n.note_id}`}
                    >
                      <span className="truncate" title={n.title}>{n.title}</span>
                      <button
                        type="button"
                        onClick={() => removeCtxNote(n.note_id)}
                        className="rounded-full p-0.5 text-slate-500 hover:bg-slate-800 hover:text-white"
                        aria-label="remove"
                        data-testid={`chat-kb-chip-remove-${n.note_id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <p className="mt-1.5 text-[10px] text-slate-500">{t("chat.kb_context_hint")}</p>
              </div>
            )}
            <div className="flex items-end gap-2 rounded-2xl border border-slate-700 bg-slate-900 p-3 focus-within:border-[#9ACD32]">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={transcribing ? t("chat.mic_transcribing") : t("chat.placeholder")}
                rows={1}
                className="max-h-40 min-h-[40px] resize-none border-0 bg-transparent text-[15px] focus-visible:ring-0 focus-visible:ring-offset-0"
                disabled={sending || transcribing}
                data-testid="chat-input"
              />
              <Button
                onClick={recording ? stopRecording : startRecording}
                disabled={sending || transcribing}
                size="icon"
                variant={recording ? "default" : "ghost"}
                className={
                  recording
                    ? "h-11 w-11 shrink-0 rounded-full bg-red-500/90 text-white hover:bg-red-500 animate-pulse sm:h-10 sm:w-10"
                    : "h-11 w-11 shrink-0 rounded-full text-slate-300 hover:bg-slate-800 hover:text-[#9ACD32] sm:h-10 sm:w-10"
                }
                style={{ touchAction: "manipulation" }}
                title={recording ? t("chat.mic_stop") : t("chat.mic_start")}
                aria-label={recording ? t("chat.mic_stop") : t("chat.mic_start")}
                data-testid="chat-mic-btn"
              >
                {transcribing ? (
                  <Loader2 className="h-5 w-5 animate-spin sm:h-4 sm:w-4" />
                ) : recording ? (
                  <Square className="h-5 w-5 sm:h-4 sm:w-4" />
                ) : (
                  <Mic className="h-5 w-5 sm:h-4 sm:w-4" />
                )}
              </Button>
              <Button
                onClick={handleSend}
                disabled={!input.trim() || sending || recording}
                size="icon"
                className="h-11 w-11 shrink-0 rounded-full bg-[#9ACD32] text-[#05073B] hover:bg-[#A8DF38] sm:h-10 sm:w-10"
                style={{ touchAction: "manipulation" }}
                data-testid="chat-send-btn"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin sm:h-4 sm:w-4" /> : <Send className="h-5 w-5 sm:h-4 sm:w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
